<template>
    <VJoyWrapper direction="column" align="stretch" row-gap="4" no-margin>
        <VJoyWrapper direction="row" wrap="nowrap" align="flex-start" item-stretch no-margin>
            <div class="profile-edit-price__input-wrapper">
                <VJoyInputNumber
                    :model-value="price"
                    data-testid="profile-edit-price-input"
                    name="price"
                    :unit="currentCurrency"
                    :min="profile!.price.minPrice"
                    :max="dailyRatePrice.max"
                    placeholder="0"
                    :invalid="errors.length > 0"
                    @update:model-value="updatePriceSelection"
                >
                    {{ t('profile.section.header.edit.price.label') }}
                </VJoyInputNumber>
                <VJoyFormError v-if="errors.length" class="joy-mt-2" :errors="errors" data-testid="profile-edition-section-price-error" />
            </div>

            <div v-if="shouldDisplayEarnings" class="profile-edit-price__input-wrapper">
                <VJoyInputNumber
                    :model-value="freelancerEarning"
                    data-testid="profile-edit-price-earning-input"
                    :unit="currentCurrency"
                    name="earning"
                    :invalid="errors.length > 0"
                    @update:model-value="updateEarningSelection"
                >
                    {{ $t('profile.wizard.price.without.fee.label') }}
                    <VJoyTooltip data-testid="profile-edit-price-tooltip" class="tooltip" event="click">
                        <VJoyIcon name="question-circle" color="neutral" />
                        <template #tooltip-content>
                            <!-- eslint-disable-next-line vue/no-v-html -->
                            <p v-html="tooltipLegend"></p>
                        </template>
                    </VJoyTooltip>
                </VJoyInputNumber>
            </div>
        </VJoyWrapper>
        <VJoyHighlight v-if="shouldDisplayPriceRecommendations" level="neutral" display-icon>
            <span v-html="priceRecommendations" />
        </VJoyHighlight>
        <VJoyCheckbox
            v-model:checked="priceHidden"
            data-testid="hide-my-daily-rate"
            :disabled="isStrategyProfile"
            :label="t('profile.section.header.numeral.hide.price.label')"
            class="profile-edit-price__checkbox"
        />
        <VJoyHighlight
            v-if="!isStrategyProfile && priceHidden"
            level="warning"
            data-testid="profile-edit-price-info-text"
            display-icon
            icon="warning-triangle"
        >
            {{ t('profile.section.header.numeral.hide.price.information.text') }}
        </VJoyHighlight>
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyInputNumber, VJoyTooltip, VJoyIcon, VJoyCheckbox, VJoyWrapper, VJoyFormError, VJoyText, VJoyHighlight} from '@maltjoy/core-vue';
    import {useProfileStore} from '~/store/profileStore';
    import {useRulesStore} from '~/store/rules/rulesStore';
    import {useStrategyProfile} from '@/composables/useStrategyProfile';

    const {t} = useTranslation();
    const profileStore = useProfileStore();
    const {profile, averagePriceCurrentFreelancer} = storeToRefs(profileStore);

    const {dailyRatePrice} = storeToRefs(useRulesStore());

    const price = defineModel<number>('price', {required: true});
    const freelancerEarning = ref<number>(calculateFreelancerEarning(price.value));
    const priceHidden = defineModel<boolean>('priceHidden', {required: true});
    defineProps<{
        errors: string[];
        currentCurrency: string;
    }>();

    function updatePriceSelection(newPrice: string) {
        price.value = Number(newPrice);
        freelancerEarning.value = calculateFreelancerEarning(+newPrice);
    }
    function calculateFreelancerEarning(newPrice: number) {
        return Math.round(newPrice * (1 - profile.value!.price.standardFeeRate / 100));
    }

    function updateEarningSelection(earning: string) {
        freelancerEarning.value = Number(earning);
        price.value = calculatePriceFromFreelancerEarning(freelancerEarning.value);
    }
    function calculatePriceFromFreelancerEarning(earning: number) {
        return Math.round(earning / (1 - profile.value!.price.standardFeeRate / 100));
    }

    const shouldDisplayEarnings = computed(() => profile.value!.price.standardFeeRate > 0);

    const tooltipLegend = computed(() => {
        const faqLink = profile.value!.price.faqLink;
        return faqLink
            ? t('profile.section.header.numeral.edit.price.legend.flat.fee', {0: faqLink})
            : t('profile.section.header.numeral.edit.price.legend');
    });

    const shouldDisplayPriceRecommendations = computed(
        () => averagePriceCurrentFreelancer.value && averagePriceCurrentFreelancer.value.minPrice && averagePriceCurrentFreelancer.value.maxPrice,
    );
    const priceRecommendations = computed(() =>
        t('profile.creation.completion.wizard.details.price.description', {
            0: averagePriceCurrentFreelancer.value!.minPrice,
            1: profile.value!.price.currency.symbol,
            2: averagePriceCurrentFreelancer.value!.maxPrice,
            3: profile.value!.price.currency.symbol,
        }),
    );

    const isStrategyProfile = computed(() => useStrategyProfile());
</script>
<style lang="scss" scoped>
    .profile-edit-price {
        &__input-wrapper {
            flex: 1;
        }

        &__checkbox {
            :deep(.joy-checkbox) {
                margin-bottom: 0 !important;
            }
        }
    }

    .tooltip {
        cursor: pointer;
        display: inline-block;
    }
</style>
